<template>
  <div class="app-root">
    <header class="site-header">
      <h1 class="app-title">Mahjong Tenpai Quiz</h1>
      <div
        v-if="hasStarted"
        class="scoreboard pill"
        :aria-label="gameOver ? 'Game over total score' : 'Total score'"
        :title="
          gameOver
            ? 'Game complete. Total accumulated score.'
            : `Accumulated total score across this ${TOTAL_ROUNDS}-round game (includes +20 bonus if waits were not revealed).`
        "
      >
        <template v-if="!gameOver">
          <span class="round-indicator" aria-label="Round number"
            >Round {{ currentRound }} / {{ TOTAL_ROUNDS }}</span
          >
          <span class="score-separator">·</span>
          <span class="total-score" aria-label="Total accumulated score">{{
            totalScore
          }}</span>
        </template>
        <template v-else>
          <strong class="total-score" aria-label="Total score">{{
            totalScore
          }}</strong>
        </template>
      </div>
      <nav class="toolbar" v-if="hasStarted">
        <button
          class="refresh-btn"
          type="button"
          :disabled="loading || (!gameOver && hasStarted && !showResult)"
          @click="gameButtonClick"
          :title="
            gameOver
              ? `Start a new ${TOTAL_ROUNDS}-quiz game`
              : showResult
                ? currentRound < TOTAL_ROUNDS
                  ? 'Go to next quiz'
                  : 'Finish game'
                : 'Answer the current quiz before proceeding'
          "
        >
          <span v-if="!loading">
            <template v-if="gameOver">↻ New Game</template>
            <template v-else-if="showResult && currentRound < TOTAL_ROUNDS"
              >Next ▶</template
            >
            <template v-else-if="showResult && currentRound === TOTAL_ROUNDS"
              >Finish</template
            >
            <template v-else>Next</template>
          </span>
          <span v-else>Loading…</span>
        </button>
      </nav>
    </header>

    <main class="content" v-if="hasStarted">
      <section v-if="error" class="panel error">
        <strong>Error:</strong> {{ error }}
        <button class="retry-btn" @click="refreshQuiz">Retry</button>
      </section>

      <section v-else-if="quiz && !gameOver" class="panel quiz">
        <div class="hand-header">
          <h2>Hand</h2>
          <button
            type="button"
            class="reset-order-btn"
            @click="resetHandOrder"
            :disabled="originalHandOrder.length === 0 || isHandInOriginalOrder"
            title="Restore the original order provided by the quiz"
          >
            Reset Order
          </button>
        </div>
        <!-- Added ref for Sortable.js -->
        <div class="tiles" ref="handContainer">
          <span
            v-for="item in handItems"
            :key="item.id"
            class="tile"
            :aria-label="`Tile ${item.code}`"
          >
            <img
              class="tile-img"
              :src="tileSrc(item.code)"
              :alt="item.code"
              loading="lazy"
            />
          </span>
        </div>

        <!-- User guess selection (1m-9m) -->
        <div class="hand-header">
          <h2>Your Guess</h2>
        </div>
        <div
          class="tiles guess-choices"
          role="group"
          aria-label="Select your guess (1m–9m)"
        >
          <button
            v-for="g in guessChoices"
            :key="g"
            type="button"
            class="tile guess"
            :disabled="showResult"
            :class="{
              selected: !showResult && selectedGuesses.has(g),
              correct:
                showResult && selectedGuesses.has(g) && quiz?.waits.includes(g),
              incorrect:
                showResult &&
                selectedGuesses.has(g) &&
                !quiz?.waits.includes(g),
              missed:
                showResult &&
                !selectedGuesses.has(g) &&
                quiz?.waits.includes(g),
            }"
            @click="toggleGuess(g)"
            :aria-pressed="
              !showResult && selectedGuesses.has(g) ? 'true' : 'false'
            "
            :title="
              showResult
                ? 'Answer locked'
                : selectedGuesses.has(g)
                  ? 'Unselect ' + g
                  : 'Select ' + g
            "
          >
            <img class="tile-img" :src="tileSrc(g)" :alt="g" loading="lazy" />
          </button>
        </div>

        <div class="answer-actions">
          <button
            type="button"
            class="check-answer-btn inline-refresh"
            :disabled="selectedGuesses.size === 0 || showResult"
            @click="checkAnswer"
            title="Check your selected waits against the correct waits"
          >
            Check my answer
          </button>
          <span
            v-if="showResult"
            class="answer-result"
            :class="{ correct: isAnswerCorrect, incorrect: !isAnswerCorrect }"
            role="status"
            aria-live="polite"
          >
            {{ isAnswerCorrect ? "Correct" : "Incorrect" }}
          </span>
        </div>

        <div class="meta">
          <!-- Timer -->
          <span
            class="timer"
            :class="{
              warning: timeLeft <= 10 && timeLeft > 5 && !timedOut,
              danger: timeLeft <= 5 && !timedOut,
              timeout: timedOut,
            }"
            aria-label="Time remaining"
            role="status"
          >
            {{ timeLeft }}s
          </span>
          <!-- Click-to-reveal wait count badge -->
          <button
            type="button"
            class="badge"
            :class="{
              good: showWaitsCount && quiz.wait_count > 3,
              concealed: !showWaitsCount,
            }"
            @click="revealWaitCount"
            :aria-pressed="showWaitsCount ? 'true' : 'false'"
            :aria-label="
              showWaitsCount ? `${quiz.wait_count} waits` : 'Reveal wait count'
            "
            title="Click to reveal wait count"
          >
            <template v-if="!showWaitsCount">? waits</template>
            <template v-else>
              {{ quiz.wait_count }} wait<span v-if="quiz.wait_count !== 1"
                >s</span
              >
            </template>
          </button>
        </div>
      </section>

      <section v-else class="panel loading-hint" v-if="!gameOver">
        <p>Preparing your first quiz…</p>
      </section>

      <section v-if="gameOver" class="panel breakdown">
        <h2>Game Over</h2>
        <p>
          Total Score: <strong>{{ totalScore }}</strong>
        </p>
        <div class="results-wrapper" v-if="roundResults.length">
          <table class="results-table">
            <thead>
              <tr>
                <th>Round</th>
                <th>Result</th>
                <th>Waits</th>
                <th>Time Left</th>
                <th>Bonus</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in roundResults" :key="r.round">
                <td>{{ r.round }}</td>
                <td>
                  <span :class="r.correct ? 'res-correct' : 'res-incorrect'">{{
                    r.correct ? "Correct" : "Wrong"
                  }}</span>
                </td>
                <td>{{ r.waits }}</td>
                <td>{{ r.timeLeft }}s</td>
                <td>
                  <span :class="r.bonus ? 'bonus-earned' : 'bonus-missed'">{{
                    r.bonus ? "+20" : "—"
                  }}</span>
                </td>
                <td>{{ r.score }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="breakdown-hint">
          Bonus (+20) is awarded only if the waits hint was not revealed.
        </p>
      </section>
    </main>
    <main class="content" v-else>
      <section class="panel intro">
        <h2>Mahjong Tenpai Quiz</h2>
        <p>
          Each game consists of {{ TOTAL_ROUNDS }} tenpai quizzes. For each
          quiz, identify all winning tiles (1m–9m) that complete the hand. You
          have 60 seconds per quiz. Scoring: if correct, you earn 10 points plus
          the number of whole seconds remaining, and a +20 bonus if you did not
          reveal the waits hint; otherwise 0. Your total is the sum over the
          {{ TOTAL_ROUNDS }} quizzes. Reorder tiles to help visualize. Revealing
          the number of waits forfeits the bonus. Press “Start Game” to begin.
        </p>
        <button class="refresh-btn start-btn" type="button" @click="startGame">
          Start Game
        </button>
      </section>
    </main>

    <footer class="site-footer">
      <small>
        Backend: Rust/Axum • Frontend: Vue 3 •
        <a href="https://github.com/" target="_blank" rel="noopener noreferrer"
          >Source (placeholder)</a
        >
      </small>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick, computed } from "vue";
// @ts-ignore - using SortableJS without installed types
import Sortable from "sortablejs";
import { useApiConfig } from "./main";

const TOTAL_ROUNDS = 10;

interface QuizResponse {
  hand: string[];
  waits: string[];
  wait_count: number;
}

const api = useApiConfig();

const quiz = ref<QuizResponse | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);

// Whether the user has revealed the wait count hint.
const showWaitsCount = ref(false);

// (gamesPlayed removed – no longer needed)
// Number of correctly answered quizzes this session

// Container ref for draggable hand tiles
const handContainer = ref<HTMLElement | null>(null);
// Will hold the Sortable instance (so we don't initialize twice)
let handSortable: any | null = null;
// Reactive wrapper with stable ids for tiles (prevents Vue diff/key issues on reorder)
const handItems = ref<{ id: string; code: string }[]>([]);
const originalHandOrder = ref<string[]>([]);

// Guess selection (tiles 1m-9m)
const guessChoices = ["1m", "2m", "3m", "4m", "5m", "6m", "7m", "8m", "9m"]; // static; could be lifted to module scope
const selectedGuesses = ref<Set<string>>(new Set());
const showResult = ref(false);
const waitsSet = ref<Set<string>>(new Set());
const hasStarted = ref(false);

const totalScore = ref(0);
const currentRound = ref(0); // 0 means not begun; 1..TOTAL_ROUNDS active rounds
const gameOver = ref(false);
const roundResults = ref<
  {
    round: number;
    correct: boolean;
    timeLeft: number;
    bonus: boolean;
    score: number;
    revealed: boolean;
    waits: number;
  }[]
>([]);

// Per-quiz countdown timer (seconds)
const timeLeft = ref(60);
const timedOut = computed(() => timeLeft.value === 0);
let timerHandle: number | null = null;

function stopTimer() {
  if (timerHandle !== null) {
    clearInterval(timerHandle);
    timerHandle = null;
  }
}

function finalizeAnswer(source: "manual" | "timeout") {
  if (showResult.value) return;
  showResult.value = true;
  stopTimer();
  let roundScore = isAnswerCorrect.value ? 10 + timeLeft.value : 0;
  const earnedBonus = roundScore > 0 && !showWaitsCount.value;
  if (earnedBonus) {
    // Bonus: solved without revealing wait count
    roundScore += 20;
  }
  totalScore.value += roundScore;

  // Record round result breakdown
  roundResults.value.push({
    round: currentRound.value,
    correct: isAnswerCorrect.value,
    timeLeft: timeLeft.value,
    bonus: earnedBonus,
    score: roundScore,
    revealed: showWaitsCount.value,
    waits: quiz.value ? quiz.value.wait_count : 0,
  });
}

function startTimer() {
  stopTimer();
  timeLeft.value = 60;
  timerHandle = window.setInterval(() => {
    timeLeft.value--;
    if (timeLeft.value <= 0) {
      timeLeft.value = 0;
      finalizeAnswer("timeout");
    }
  }, 1000);
}

const isAnswerCorrect = computed(() => {
  if (!quiz.value) return false;
  if (waitsSet.value.size !== selectedGuesses.value.size) return false;
  for (const g of selectedGuesses.value) {
    if (!waitsSet.value.has(g)) return false;
  }
  return true;
});
function checkAnswer() {
  if (!quiz.value || showResult.value) return;
  finalizeAnswer("manual");
}

function nextQuiz() {
  if (gameOver.value) return;
  if (currentRound.value < TOTAL_ROUNDS) {
    currentRound.value++;

    showResult.value = false;
    refreshQuiz();
  }
}
function startNewGame() {
  gameOver.value = false;
  totalScore.value = 0;
  roundResults.value = [];
  currentRound.value = 1;
  refreshQuiz();
}
function gameButtonClick() {
  if (loading.value) return;
  if (gameOver.value) {
    startNewGame();
  } else if (showResult.value) {
    if (currentRound.value < TOTAL_ROUNDS) {
      nextQuiz();
    } else {
      gameOver.value = true;
    }
  }
}
function toggleGuess(code: string) {
  // Once result is shown, selection is locked.
  if (showResult.value) return;
  if (selectedGuesses.value.has(code)) {
    selectedGuesses.value.delete(code);
  } else {
    selectedGuesses.value.add(code);
  }
  // Reassign a new Set instance to maintain Vue reactivity
  selectedGuesses.value = new Set(selectedGuesses.value);
  // Any change invalidates previous result until re-checked
  showResult.value = false;
}

// Derived state: whether current visual order matches original quiz order
const isHandInOriginalOrder = computed(() => {
  if (!quiz.value) return true;
  if (originalHandOrder.value.length !== quiz.value.hand.length) return false;
  return quiz.value.hand.every((c, i) => c === originalHandOrder.value[i]);
});

// Reset the hand order back to the original initial order
function resetHandOrder() {
  if (!quiz.value || originalHandOrder.value.length === 0) return;
  const ts = Date.now();
  handItems.value = originalHandOrder.value.map((code, i) => ({
    id: `${ts}-reset-${i}-${Math.random().toString(36).slice(2, 8)}`,
    code,
  }));
  quiz.value.hand = [...originalHandOrder.value];
  console.debug("[hand] order reset", quiz.value.hand);
}

/**
 * Fetch a new quiz from the backend.
 * The waits panel starts collapsed; the wait count is hidden until the user clicks the badge.
 */
async function refreshQuiz() {
  loading.value = true;
  error.value = null;
  try {
    const res = await fetch(api.url("/api/quiz"), {
      headers: {
        Accept: "application/json",
      },
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const data: QuizResponse = await res.json();
    quiz.value = data;
    originalHandOrder.value = [...data.hand];
    waitsSet.value = new Set(data.waits);
    // Build stable-id items for the hand (ids persist across reorders)
    const ts = Date.now();
    handItems.value = data.hand.map((code, i) => ({
      id: `${ts}-${i}-${Math.random().toString(36).slice(2, 8)}`,
      code,
    }));
    // Hide the waits count again for the new quiz.
    showWaitsCount.value = false;
    // Clear any previous guesses and reset result state.
    selectedGuesses.value = new Set();
    showResult.value = false;

    // Start (or restart) the per-quiz timer
    startTimer();
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : "Unknown error";
    quiz.value = null;
  } finally {
    loading.value = false;
  }
}

function revealWaitCount() {
  if (!showWaitsCount.value) {
    showWaitsCount.value = true;
  }
}

/**
 * Resolve a tile code like "1m" to its SVG asset.
 * Assumes files are placed in ./assets named exactly "<code>.svg".
 * Falls back to the text code (empty string src) if resolution fails.
 */
function tileSrc(code: string): string {
  try {
    return new URL(`./assets/${code}.svg`, import.meta.url).href;
  } catch {
    return "";
  }
}

// Initial load waits for user to start the game.
onMounted(() => {
  // User will press Start Game; no automatic fetch here.
});

function startGame() {
  if (hasStarted.value) return;
  hasStarted.value = true;
  gameOver.value = false;
  totalScore.value = 0;

  currentRound.value = 1;
  refreshQuiz();
}

// Initialize Sortable when the hand first becomes available (only once)
watch(
  () => handItems.value.length,
  async (len) => {
    if (!len) return;
    await nextTick();
    if (handContainer.value && !handSortable) {
      console.debug("[hand] initializing sortable");
      handSortable = Sortable.create(handContainer.value, {
        animation: 150,
        forceFallback: true,
        fallbackOnBody: true,
        fallbackTolerance: 3,
        ghostClass: "drag-ghost",
        chosenClass: "drag-chosen",
        dragClass: "drag-dragging",
        direction: "horizontal",
        onStart() {
          console.debug("[hand] drag start");
        },
        onEnd(evt: any) {
          if (!quiz.value) return;
          const oldIndex = evt.oldIndex ?? -1;
          const newIndex = evt.newIndex ?? -1;
          if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex)
            return;
          // Reorder the stable-id list first
          const updated = handItems.value.slice();
          const moved = updated.splice(oldIndex, 1)[0];
          updated.splice(newIndex, 0, moved);
          handItems.value = updated;
          // Sync quiz.hand (business / backend order) from new visual order
          quiz.value.hand = handItems.value.map((i) => i.code);
        },
      });
    }
  },
  { immediate: true },
);

// If API base changes (unlikely at runtime) refetch.
watch(
  () => api.baseUrl,
  () => {
    if (!loading.value) refreshQuiz();
  },
);
</script>

<style scoped>
/* Layout */
.app-root {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.site-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--border, #d0d7de);
  background: linear-gradient(
    90deg,
    rgba(37, 99, 235, 0.08),
    rgba(37, 99, 235, 0)
  );
  backdrop-filter: blur(6px);
}

.app-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.games-played {
  font-size: 0.6em;
  font-weight: 500;
  opacity: 0.8;
  background: #eceff4;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  line-height: 1;
}

.scoreboard {
  flex: 1;
  text-align: center;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  opacity: 0.85;
}

@media (prefers-color-scheme: dark) {
  .games-played {
    background: #2d3640;
    opacity: 0.9;
  }
  .scoreboard {
    opacity: 0.9;
  }
}

.toolbar {
  display: flex;
  gap: 0.5rem;
}

.content {
  flex: 1;
  width: 100%;
  max-width: 950px;
  margin: 0 auto;
  padding: 1rem clamp(0.75rem, 2vw, 1.25rem);
  box-sizing: border-box;
}

.site-footer {
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  text-align: center;
  border-top: 1px solid var(--border, #d0d7de);
  background: var(--footer-bg, rgba(0, 0, 0, 0.02));
}

.site-footer a {
  color: var(--accent, #2563eb);
  text-decoration: none;
}

.site-footer a:hover {
  text-decoration: underline;
}

/* Panels */
.panel {
  border: 1px solid var(--border, #d0d7de);
  border-radius: 8px;
  padding: 1rem 1.1rem 1.2rem;
  background: var(--panel-bg, #fff);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  position: relative;
}

@media (prefers-color-scheme: dark) {
  .panel {
    background: #1c222b;
    border-color: #2a3038;
  }
}

.panel + .panel {
  margin-top: 1.25rem;
}

.quiz h2 {
  margin-top: 0;
  margin-bottom: 0.75rem;
  font-size: 1.1rem;
  font-weight: 600;
}

.hand-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.reset-order-btn {
  cursor: pointer;
  border: 1px solid #888ea3;
  background: linear-gradient(90deg, #f2f4f8, #e6e9ef);
  color: #374151;
  font-weight: 500;
  line-height: 1.1;
  padding: 0.4rem 0.7rem;
  font-size: 0.65rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.reset-order-btn:hover:not(:disabled) {
  background: linear-gradient(90deg, #e9ecf2, #dde1e8);
}

.reset-order-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (prefers-color-scheme: dark) {
  .reset-order-btn {
    background: #2d3640;
    border-color: #3a4653;
    color: #cdd4dc;
  }
  .reset-order-btn:hover:not(:disabled) {
    background: #35404c;
  }
}

.loading-hint {
  text-align: center;
  font-style: italic;
}

.intro {
  max-width: 760px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.intro p {
  margin: 0;
  line-height: 1.4;
  font-size: 0.9rem;
  opacity: 0.85;
}

.start-btn {
  align-self: flex-start;
}

/* Tiles */
.tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-bottom: 0.85rem;
}

.tile {
  display: inline-flex;
  padding: 0;
  border: none;
  background: #eeeeee;
  border-radius: 4px;
  cursor: grab;
  user-select: none;
}

.tile:active {
  cursor: grabbing;
}

.tile.wait {
  outline: 2px solid #c2e6a1;
  border-radius: 4px;
}

.tile.guess {
  cursor: pointer;
  background: #f2f5f9;
  border: none;
  outline: 2px solid transparent;
  transition:
    background 0.15s ease,
    outline-color 0.15s ease;
}

.tile.guess:hover,
.tile.guess:focus-visible {
  background: #e3e9f0;
}

.tile.guess.selected {
  background: #e0edff;
  outline-color: var(--accent, #2563eb);
}

.tile.guess.correct {
  outline-color: #16a34a;
  background: #d1fadf;
}

.tile.guess.incorrect {
  outline-color: #dc2626;
  background: #fee2e2;
}

.tile.guess.missed {
  outline-color: #ec4899;
  background: #fde3f1;
}

.answer-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.25rem 0 0.5rem;
}

.answer-result {
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.answer-result.correct {
  color: #16a34a;
}

.answer-result.incorrect {
  color: #dc2626;
}

@media (prefers-color-scheme: dark) {
  .tile.guess.correct {
    background: #1f3b20;
    outline-color: #16a34a;
  }
  .tile.guess.incorrect {
    background: #452626;
    outline-color: #dc2626;
  }
  .tile.guess.missed {
    background: #4a243d;
    outline-color: #ec4899;
  }
}

.tile-img {
  display: block;
  width: 48px;
  height: 64px;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  background: transparent;
}

.drag-ghost {
  opacity: 0.4;
}
.drag-chosen {
  outline: 2px solid var(--accent, #2563eb);
  outline-offset: 0;
}
.drag-dragging {
  opacity: 0.7;
  transform: scale(0.98);
}

@media (prefers-color-scheme: dark) {
  .tile.wait {
    outline-color: #426232;
  }
  .tile.guess {
    background: #2a323d;
  }
  .tile.guess:hover,
  .tile.guess:focus-visible {
    background: #343e4a;
  }
  .tile.guess.selected {
    background: #1f314a;
    outline-color: var(--accent, #2563eb);
  }
}

/* Buttons */
.refresh-btn,
.inline-refresh,
.retry-btn {
  cursor: pointer;
  border: 1px solid var(--accent, #2563eb);
  background: var(--accent, #2563eb);
  color: #fff;
  font-weight: 500;
  line-height: 1.1;
  padding: 0.55rem 1rem;
  font-size: 0.85rem;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  transition:
    background 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

.refresh-btn:hover,
.inline-refresh:hover,
.retry-btn:hover {
  background: var(--accent-hover, #1d4ed8);
}

.refresh-btn:disabled,
.inline-refresh:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.inline-refresh {
  padding: 0.4rem 0.65rem;
  font-size: 0.7rem;
  background: linear-gradient(90deg, #2563eb, #1e4fb7);
  border-color: #1e4fb7;
}

.inline-refresh:hover {
  background: linear-gradient(90deg, #1e4fb7, #183f92);
}

.retry-btn {
  margin-left: 0.75rem;
}

/* Waits section */
.waits summary {
  cursor: pointer;
  font-weight: 600;
  margin-bottom: 0.4rem;
  outline: none;
  list-style: none;
}

.waits summary::-webkit-details-marker {
  display: none;
}

.waits summary::before {
  content: "▶";
  display: inline-block;
  margin-right: 0.4rem;
  transform: rotate(0deg);
  transition: transform 0.15s ease;
  font-size: 0.7rem;
  vertical-align: middle;
  opacity: 0.75;
}

.waits[open] summary::before {
  transform: rotate(90deg);
  opacity: 0.95;
}

.waits-tiles {
  margin-top: 0.25rem;
}

/* Meta info */
.meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.4rem;
}

.badge {
  background: #eceff4;
  color: #374151;
  padding: 0.25rem 0.55rem;
  font-size: 0.7rem;
  border-radius: 999px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-weight: 600;
  border: 1px solid #d6d9de;
  cursor: default;
  user-select: none;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
}

.badge.concealed {
  cursor: pointer;
  background: #dfe3ea;
  border-style: dashed;
  border-color: #c0c6ce;
}

.badge.concealed:hover,
.badge.concealed:focus-visible {
  background: #d3d8e0;
}

.badge.good {
  background: #dbf9db;
  border-color: #b7e4b7;
  color: #225e22;
}

/* Timer */
.timer {
  background: #eceff4;
  color: #1f2937;
  padding: 0.25rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 999px;
  letter-spacing: 0.5px;
  border: 1px solid #d6d9de;
  min-width: 2.4rem;
  text-align: center;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.timer.warning {
  background: #fff7e0;
  border-color: #f2d48a;
  color: #7a5300;
}
.timer.danger {
  background: #ffe5e5;
  border-color: #f5b5b5;
  color: #a52323;
}
.timer.timeout {
  background: #2d2d2d;
  color: #ffffff;
  border-color: #444;
}

@media (prefers-color-scheme: dark) {
  .timer {
    background: #2d3640;
    border-color: #3a4653;
    color: #e5ecf3;
  }
  .timer.warning {
    background: #4a4125;
    border-color: #6b572a;
    color: #fcd77a;
  }
  .timer.danger {
    background: #4b2d2d;
    border-color: #653d3d;
    color: #ffb3b3;
  }
  .timer.timeout {
    background: #452f49;
    border-color: #5e3e64;
    color: #ffd9ff;
  }
}

@media (prefers-color-scheme: dark) {
  .badge {
    background: #2d3640;
    border-color: #3a4653;
    color: #cdd4dc;
  }
  .badge.concealed {
    background: #384450;
    border-color: #4a5866;
  }
  .badge.concealed:hover,
  .badge.concealed:focus-visible {
    background: #42505d;
  }
  .badge.good {
    background: #1f3b20;
    border-color: #335a34;
    color: #8fd88f;
  }
}

/* Error panel */
.error {
  border-color: #ffb4b4;
  background: #fff3f3;
  color: #660b0b;
}

@media (prefers-color-scheme: dark) {
  .error {
    border-color: #7a2c2c;
    background: #3a1e1e;
    color: #ffdddd;
  }
}

/* Scrollbars (webkit) */
.tiles::-webkit-scrollbar {
  height: 8px;
}
.tiles::-webkit-scrollbar-track {
  background: transparent;
}
.tiles::-webkit-scrollbar-thumb {
  background: #d0d7de;
  border-radius: 4px;
}
@media (prefers-color-scheme: dark) {
  .tiles::-webkit-scrollbar-thumb {
    background: #3a4653;
  }
}

/* Accessibility focus */
button:focus-visible,
summary:focus-visible {
  outline: 2px solid var(--accent, #2563eb);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Animations / transitions (subtle) */
.panel,
.tile,
.refresh-btn,
.inline-refresh {
  transition:
    box-shadow 0.2s ease,
    transform 0.15s ease;
}

.refresh-btn:active,
.inline-refresh:active {
  transform: translateY(1px);
}

.panel:hover {
  box-shadow: 0 3px 10px -4px rgba(0, 0, 0, 0.15);
}

@media (prefers-reduced-motion: reduce) {
  * {
    animation: none !important;
    transition: none !important;
  }
}

/* Post-game breakdown */
.breakdown .results-wrapper {
  overflow-x: auto;
  margin-top: 0.75rem;
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}

.results-table th,
.results-table td {
  padding: 0.4rem 0.5rem;
  text-align: center;
  border-bottom: 1px solid #d6d9de;
  white-space: nowrap;
}

.results-table th {
  background: #f1f4f8;
  font-weight: 600;
  letter-spacing: 0.5px;
}

@media (prefers-color-scheme: dark) {
  .results-table th {
    background: #2a323b;
  }
  .results-table th,
  .results-table td {
    border-bottom-color: #3a4653;
  }
}

.res-correct {
  color: #16a34a;
  font-weight: 600;
}

.res-incorrect {
  color: #dc2626;
  font-weight: 600;
}

.bonus-earned {
  color: #1d4ed8;
  font-weight: 600;
}

.bonus-missed {
  opacity: 0.4;
}

.breakdown-hint {
  margin-top: 0.75rem;
  font-size: 0.65rem;
  opacity: 0.75;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
</style>
